import moment from 'moment';
import * as xlsx from 'xlsx';

export const MAX_DATE_MS = 8640000000000000;

export const DAY_MS = 1000 * 60 * 60 * 24;

export type DateRange = {
  min: string;
  max: string;
};

export function daysBetween(dates: DateRange) {
  return moment(dates.max).diff(moment(dates.min), 'days');
}

export function annualize(dates: DateRange, value: number) {
  return moment(dates.max).diff(moment(dates.min), 'days') / 365.25;
}

export function normalizeDate(date: string | null): string | null {
  if (!date) {
    return null;
  }
  const parsed = moment(
    date,
    ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', 'MM/DD/YY', 'M/D/YY', 'YYYYMMDD'],
    true,
  ).utc();

  if (!parsed.isValid()) {
    return null;
  }

  return parsed.format('YYYY-MM-DD');
}

export function normalizeTime(time: string | null): string | null {
  if (!time) {
    return null;
  }
  let parsed = moment(time, ['HHmm', 'HH:mm', 'hh:mm A', 'hh:mm a'], true);

  if (!parsed.isValid()) {
    parsed = moment(time, ['H:mm', 'h:mm A', 'h:mm a'], true);
    if (!parsed.isValid()) {
      return null;
    }
  }

  return parsed.format('HH:mm');
}

export function normalizeBirthdate(birthdate: string | null): string | null {
  if (!birthdate) {
    return null;
  }
  let parsed = moment(
    birthdate,
    ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY'],
    true,
  ).utc();

  if (!parsed.isValid()) {
    parsed = moment(birthdate, ['MM/DD/YY', 'M/D/YY', 'YYYYMMDD'], true).utc();
    if (!parsed.isValid()) {
      if (/^\d+$/.test(birthdate)) {
        // assume xlsx date
        const xlDate = xlsx.SSF.parse_date_code(parseInt(birthdate));
        if (!xlDate) {
          return null;
        }
        parsed = moment(
          `${xlDate.m}/${xlDate.d}/${xlDate.y}`,
          'M/D/YYYY',
        ).utc();
        if (!parsed.isValid()) {
          return null;
        }
      } else {
        return null;
      }
    } else {
      // special handling of two digit years
      // by default 69 and up will be considered to be 1900s
      // and 68 and below are considered 2000s
      // we need to make sure we never have future birthdates
      if (moment().utc().isBefore(parsed)) {
        parsed.subtract(100, 'year');
      }
    }
  }

  return parsed.format('YYYY-MM-DD');
}

// get the number of days that have passed since this date
export function getDaysSinceDate(dateString: string): number {
  const currentDate = new Date();
  const givenDate = new Date(dateString);

  // Calculate the time difference in milliseconds
  const timeDifference = currentDate.getTime() - givenDate.getTime();

  // Convert milliseconds to days
  const millisecondsInADay = 1000 * 60 * 60 * 24;
  const daysDifference = Math.floor(timeDifference / millisecondsInADay);

  return daysDifference;
}

/**
 * Get the day of month from a date and format it with a leading 0 as needed.
 * Should return as 'DD', with some value between '01' and '31'.
 *  */
export function getDayOfMonthFromDate(date: Date | null): string {
  if (!date) {
    return '';
  }
  const day: number = date.getDate();

  if (day < 10) {
    return '0' + day.toString();
  }
  return day.toString();
}

/**
 * Get the month from a date and format it with a leading 0 as needed.
 * Should return as 'MM', with some value between '01' and '12'.
 *  */
export function getMonthFromDate(date: Date | null): string {
  if (!date) {
    return '';
  }
  // Month is zero-indexed, but year and day aren't.
  const month: number = date.getMonth() + 1;

  if (month < 10) {
    return '0' + month.toString();
  }
  return month.toString();
}

/**
 * Get the full year from a date.
 * Should return as 'YYYY'.
 *  */
export function getFullYearFromDate(date: Date | null): string {
  if (!date) {
    return '';
  }
  return date.getFullYear().toString();
}

/**
 * Format a date to U.S. style (i.e. middleEndian) MM-DD-YYYY
 * returns a blank string if the date is invalid
 */
export function middleEndianDate(date: string): string {
  const m = moment(date);
  return m.isValid() ? m.utc().format('MM-DD-YYYY') : '';
}

export function isoDate(date: Date | string | moment.Moment) {
  return moment(date).utc().format('YYYY-MM-DD');
}

export function isoTime(time: moment.Moment) {
  return moment(time).format('HH:mm');
}

export function isoDateTime(date: Date | string) {
  return moment(date).utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]');
}

export function utcDate(date: Date) {
  return moment(date).utc().format('MM/DD/YYYY');
}

export function addDays(date: string, numDays: number): string {
  return isoDate(new Date(new Date(date).getTime() + DAY_MS * numDays));
}

function isLastDayOfMonth(date: Date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const nextDay = new Date(year, month, date.getDate() + 1);

  return nextDay.getMonth() !== month;
}

export function getInterval(start: string, end: string) {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const yearStartDate = new Date(start);
  yearStartDate.setFullYear(yearStartDate.getFullYear() + 1);
  yearStartDate.setDate(yearStartDate.getDate() - 1);

  if (yearStartDate.getTime() == endDate.getTime()) {
    startDate.setFullYear(startDate.getFullYear() + 1);
    endDate.setFullYear(endDate.getFullYear() + 1);
    return { start: isoDate(startDate), end: isoDate(endDate) };
  }

  for (const monthInterval of [9, 6, 3, 2, 1]) {
    const monthStartDate = new Date(start);
    monthStartDate.setMonth(monthStartDate.getMonth() + monthInterval);
    monthStartDate.setDate(monthStartDate.getDate() - 1);
    if (monthStartDate.getTime() === endDate.getTime()) {
      startDate.setMonth(startDate.getMonth() + monthInterval);
      if (isLastDayOfMonth(endDate)) {
        endDate.setDate(1);
        endDate.setMonth(endDate.getMonth() + monthInterval + 1);
        endDate.setDate(endDate.getDate() - 1);
      } else {
        endDate.setMonth(endDate.getMonth() + monthInterval);
      }
      return { start: isoDate(startDate), end: isoDate(endDate) };
    }
  }

  const dayInterval =
    Math.floor((endDate.getTime() - startDate.getTime()) / DAY_MS) + 1;
  startDate.setDate(startDate.getDate() + dayInterval);
  endDate.setDate(endDate.getDate() + dayInterval);
  return { start: isoDate(startDate), end: isoDate(endDate) };
}

/**
 * Get array of date strings in range [start, end) according to
 * UTC startOf('day').
 */
export function datesInRange(start: string, end: string) {
  const dates = [];
  const curDate = moment(start).startOf('day');
  const endDate = moment(end).startOf('day');

  while (curDate.diff(endDate) < 0) {
    dates.push(isoDate(curDate.clone().toDate()));
    curDate.add(1, 'days');
  }

  return dates;
}

export function monthsInRange(start: string, end: string) {
  const dates = [];
  const curDate = moment(start).date(15);
  const endDate = moment(end).date(15);

  while (curDate.diff(endDate) <= 0) {
    dates.push(isoDate(curDate.clone().toDate()));
    curDate.add(1, 'month');
  }

  return dates;
}
